import type { LeagueFixture } from "../types/index.ts";
import type { ClassicMatchOdds } from "../utils/tournamentScoring.ts";

const SUPABASE_URL = "https://fvuwfonfrllbjqvywula.supabase.co";

const anonHeaders = (): Record<string, string> => ({
  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

const PROJECT_REF = "fvuwfonfrllbjqvywula";
const SESSION_STORAGE_KEY = `sb-${PROJECT_REF}-auth-token`;

const getAccessTokenFromStorage = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.access_token ?? parsed?.currentSession?.access_token ?? null;
  } catch {
    return null;
  }
};

const authedHeaders = (): Record<string, string> => {
  const token = getAccessTokenFromStorage() ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Read access to the persisted Liga single-match schedule (league_fixtures,
 * Migration 042) + the admin-only preview-odds upkeep used while the odds
 * API integration (The Odds API — a deferred later step) isn't wired up
 * yet. Fixtures/results themselves are written exclusively by the
 * league-resolver Edge Function (service_role, bypasses RLS) — this
 * service is read-only for those columns.
 */
export class LeagueFixtureService {
  private static instance: LeagueFixtureService;
  private constructor() {}

  public static getInstance(): LeagueFixtureService {
    if (!LeagueFixtureService.instance) {
      LeagueFixtureService.instance = new LeagueFixtureService();
    }
    return LeagueFixtureService.instance;
  }

  /** All fixtures for a league, optionally scoped to one matchday. */
  async getFixtures(leagueId: number, matchday?: number): Promise<LeagueFixture[]> {
    try {
      const matchdayFilter = matchday != null ? `&matchday=eq.${matchday}` : "";
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/league_fixtures?league_id=eq.${leagueId}${matchdayFilter}&select=*&order=kickoff_utc`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!res.ok) return [];
      return (await res.json()) as LeagueFixture[];
    } catch (e) {
      console.error("LeagueFixtureService.getFixtures failed:", e);
      return [];
    }
  }

  /** All fixtures for a league across every matchday — used for scoring/history. */
  async getAllFixtures(leagueId: number): Promise<LeagueFixture[]> {
    return this.getFixtures(leagueId);
  }

  /**
   * The matchday to show the tip UI for: the lowest not-yet-finished
   * matchday with fixtures on record, or the highest known matchday if all
   * are finished (season over / nothing left to tip).
   */
  async getCurrentMatchday(leagueId: number): Promise<number | null> {
    const fixtures = await this.getAllFixtures(leagueId);
    if (fixtures.length === 0) return null;
    const matchdays = [...new Set(fixtures.map((f) => f.matchday))].sort((a, b) => a - b);
    for (const md of matchdays) {
      const mdFixtures = fixtures.filter((f) => f.matchday === md);
      if (mdFixtures.some((f) => !f.is_finished)) return md;
    }
    return matchdays[matchdays.length - 1] ?? null;
  }

  /**
   * Admin-only: set/update the preview odds for a fixture (Migration 042
   * league_fixtures_admin_update policy, email-gated). Manual stand-in until
   * The Odds API integration (deferred step) fills these automatically.
   * Never touches the locked odds — those are frozen exclusively by the
   * league-resolver Edge Function at matchday kickoff.
   */
  async setPreviewOdds(fixtureId: number, odds: ClassicMatchOdds): Promise<boolean> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/league_fixtures?id=eq.${fixtureId}`,
        {
          method: "PATCH",
          headers: { ...authedHeaders(), Prefer: "return=minimal" },
          body: JSON.stringify({
            odds_home: odds.home,
            odds_draw: odds.draw,
            odds_away: odds.away,
            updated_at: new Date().toISOString(),
          }),
        },
      );
      return res.ok;
    } catch (e) {
      console.error("LeagueFixtureService.setPreviewOdds failed:", e);
      return false;
    }
  }

  /**
   * Admin-only: run the league-resolver by hand.
   *
   * The resolver normally runs from a Coolify cron every 10 minutes. When
   * that cron is broken, no result ever reaches league_fixtures and the
   * classic-mode tips keep showing no Endstand — this is the manual way out
   * that doesn't need shell access. Authorisation happens inside the
   * function against the logged-in user's JWT (platform admin only), so the
   * cron secret never has to exist in the browser.
   *
   * The resolver talks to several external APIs; a full run takes ~30s.
   */
  async triggerResolverSync(): Promise<{ ok: boolean; report: unknown }> {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/league-resolver`, {
        method: "POST",
        headers: authedHeaders(),
        body: "{}",
      });
      const body = await res.json().catch(() => null);
      return { ok: res.ok, report: body };
    } catch (e) {
      console.error("LeagueFixtureService.triggerResolverSync failed:", e);
      return { ok: false, report: String(e) };
    }
  }

  /**
   * Deadline source of record: matchday_schedule.first_kickoff_utc per
   * matchday (Migration 026), written by the league-resolver Edge Function.
   * Kept separate from each fixture's own kickoff_utc so the deadline can
   * diverge from what's displayed (e.g. an admin-adjusted test deadline)
   * without corrupting the real kickoff time shown on the fixture cards.
   * Returns a Map of matchday -> first_kickoff_utc in ms; matchdays without
   * a row are simply absent (caller falls back to fixture-derived kickoff).
   */
  async getMatchdayDeadlines(leagueId: number): Promise<Map<number, number>> {
    try {
      // matchday_schedule ist per RLS (Migration 026) nur für auth.role() =
      // 'authenticated' lesbar — mit dem nackten anon-Key kommt still ein
      // leeres Array zurück. Deshalb hier die Session des Nutzers mitschicken.
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/matchday_schedule?league_id=eq.${leagueId}&select=matchday,first_kickoff_utc`,
        { method: "GET", headers: authedHeaders() },
      );
      if (!res.ok) return new Map();
      const rows = (await res.json()) as { matchday: number; first_kickoff_utc: string }[];
      return new Map(rows.map((r) => [r.matchday, new Date(r.first_kickoff_utc).getTime()]));
    } catch (e) {
      console.error("LeagueFixtureService.getMatchdayDeadlines failed:", e);
      return new Map();
    }
  }

  /** Extracts the frozen odds (or null if not yet locked) as ClassicMatchOdds. */
  static lockedOdds(fixture: LeagueFixture): ClassicMatchOdds | null {
    if (fixture.odds_locked_at == null) return null;
    return {
      home: fixture.odds_home_locked,
      draw: fixture.odds_draw_locked,
      away: fixture.odds_away_locked,
    };
  }
}

export const leagueFixtureService = LeagueFixtureService.getInstance();
